import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import AlchemyObject from "./Objects/Object";
import { DragControls } from "three/examples/jsm/controls/DragControls";
import { controls } from "./App";
import { combine, instantiate } from "./Objects/Combinator";
import GenericObject from "./Objects/GenericObject";
import { LIBRARY, Metadata } from "./Objects/Constants";
import { XRButton } from "three/examples/jsm/webxr/XRButton";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { ToastContainer, toast } from "react-toastify";

export let dragControls: DragControls;

type EraSchema = {
  [key: number]: {
    requirements: string[];
    name: string;
    newObjects: string[]; // Objects available at the start of the era
    message: string;
  };
};

export const eraSchemas: EraSchema = {
  0: {
    requirements: [], // Initial era has no requirements
    name: "Genesis Era",
    newObjects: ["air", "earth", "fire", "water"], // Adjusted to match the new schema
    message:
      "In the beginning, all that existed were air, earth, fire, and water.",
  },
  1: {
    requirements: ["tree"],
    name: "New Life Era",
    newObjects: ["human"], // Adjusted to match the new schema
    message:
      "During the dawn of civilization, humans lived in harmony with our environment.",
  },
  2: {
    requirements: ["trash", "coal"],
    name: "Human Footprint Era",
    newObjects: [], // Adjusted to match the new schema
    message:
      "As civilization advanced, human activities began markedly shaping the Earth's landscapes and ecosystems.",
  },
  3: {
    requirements: ["factory", "car", "mine", "industrial_farm"],
    name: "Industrial Era",
    newObjects: [], // Adjusted to match the new schema
    message:
      "The rise of industry turbocharged human impact, significantly altering climate and biodiversity with widespread pollution and resource exploitation.",
  },
  4: {
    requirements: [
      "community_garden",
      "electric_car",
      "recycling_facility",
      "wind_turbine",
      "solar_panel",
    ],
    name: "New Energy Era",
    newObjects: [], // Adjusted to match the new schema
    message:
      "With innovation and raising awareness, we can harness renewable resources to restore balance and harmony with nature.",
  },
};

class GameScene extends THREE.Scene {
  mainCamera: THREE.Camera;
  renderer: THREE.Renderer;
  ambientLight: THREE.Light;
  directionalLight: THREE.DirectionalLight;
  setHoveredItem?: (typeId: string | undefined) => void;
  setEra?: (eraOrder: number) => void;
  eraOrder?: number;
  unlockObject?: (typeId: string) => void;

  // On drag
  offsetX = 0;
  offsetZ = 0;
  dragStartPosition?: THREE.Vector3;

  sceneNames = new Array<String>();
  sceneList = new Map<String, THREE.Object3D>();
  objectList = new Map<number, AlchemyObject>();
  unlockEra: any;
  checkEraProgress: any;

  constructor(mainCamera: THREE.Camera, renderer: THREE.Renderer) {
    super();

    this.mainCamera = mainCamera;
    this.renderer = renderer;
    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.ambientLight = ambientLight;
    this.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    this.directionalLight = directionalLight;
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.position.set(5, 5, 5);
    this.add(directionalLight);

    this.background = new THREE.Color(0xebf9ff);

    this.updateDragControl();

    Promise.all([
      this.addObject(new GenericObject("air"), new THREE.Vector3(-3, 0, 1)), // Moved from -2 to -3 for equal spacing
      this.addObject(new GenericObject("earth"), new THREE.Vector3(-1, 0, 1)), // Remains the same
      this.addObject(new GenericObject("fire"), new THREE.Vector3(1, 0, 1)), // Remains the same
      this.addObject(new GenericObject("water"), new THREE.Vector3(3, 0, 1)), // Moved from 2 to 3 for equal spacing
    ]).then(() => {
      this.updateDragControl();
    });
    // Promise.all(
    //     Object.keys(LIBRARY).map((value, i) => {
    //         const row = Math.floor(i / 6)
    //         const col = i % 6
    //         return this.addObject(new GenericObject(value), new THREE.Vector3(3 - row * 1.5, 0, 8 - col * 1.5))
    //     })
    // ).then(() => {
    //     this.updateDragControl()
    // })

    const glb_array = [
      "car",
      "coal",
      "community_garden",
      "electric_car",
      "factory",
      "industrial_farm",
      "mine",
      "recycling_facility",
      "solar_panel",
      "trash",
      "wind_turbine",
    ];

    // let nestedDictionary  = {
    //     "car": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "coal": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "community_garden": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "electric_car": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "factory": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "industrial_farm": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "mine": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "recycling_facility": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "solar_panel": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "trash": {"scale": 1.0, "x": 4, "y": 4, "z": 4},
    //     "wind_turbine": {"scale": 1.0, "x": 4, "y": 4, "z": 4}
    //   }

    // for (const element of glb_array) {
    //     console.log(element);
    //     new GLTFLoader().loadAsync("/scenes/" + element + ".glb").then(glb => {
    //         const scene = glb.scene
    //         scene.scale.multiplyScalar(nestedDictionary[element]!)
    //         scene.position.z = 4
    //         scene.rotateX(Math.PI / 2)
    //         scene.visible = false;
    //         if (element === 'layout_starter') {
    //             scene.visible = true;
    //         }
    //         this.sceneList.set(element, scene);
    //         this.sceneNames.push(element)
    //         this.add(scene)
    //     })
    // }

    const layout_array = [
      "layout_footprint",
      "layout_industrial",
      "layout_plants",
      "layout_starter",
      "layout_trees",
      "layout_future",
    ];

    for (const element of layout_array) {
      console.log(element);
      new GLTFLoader().loadAsync("/scenes/" + element + ".glb").then((glb) => {
        const scene = glb.scene;
        scene.scale.multiplyScalar(0.3);
        scene.position.z = 4;
        scene.rotateX(Math.PI / 2);
        scene.visible = false;
        if (element === "layout_starter") {
          scene.visible = true;
        }
        this.sceneList.set(element, scene);
        this.sceneNames.push(element);
        this.add(scene);
      });
    }
  }

  async updateDragControl() {
    let controllable: Promise<THREE.Object3D>[] = [];
    this.objectList.forEach(
      (obj: {
        getAsset: () => Promise<THREE.Object3D<THREE.Object3DEventMap>>;
      }) => {
        controllable.push(obj.getAsset());
      }
    );

    Promise.all(controllable).then((objs) => {
      if (dragControls) dragControls.dispose();
      dragControls = new DragControls(
        objs,
        this.mainCamera,
        this.renderer.domElement
      );
      dragControls.addEventListener("dragstart", (e) => {
        controls.enabled = false;
        const XZ = dragControls
          .getRaycaster()
          .ray.intersectPlane(
            new THREE.Plane(new THREE.Vector3(0, 1, 0)),
            new THREE.Vector3()
          );
        this.dragStartPosition = e.object.position.clone();
        if (XZ) {
          this.offsetX = XZ.x - e.object.position.x;
          this.offsetZ = XZ.z - e.object.position.z;
        }
      });

      dragControls.addEventListener("dragend", (e) => this.dragEnd(e.object));

      dragControls.addEventListener("hoveron", (e) => {
        const alchemyObject = this.objectList.get(e.object.id);
        if (alchemyObject) {
          this.setHoveredItem?.(alchemyObject.type_id);
        }
      });

      dragControls.addEventListener("hoveroff", (e) => {
        this.setHoveredItem?.(undefined);
      });

      dragControls.addEventListener("drag", (e) => {
        e.object.position.setY(0);
        const XZ = dragControls
          .getRaycaster()
          .ray.intersectPlane(
            new THREE.Plane(new THREE.Vector3(0, 1, 0)),
            new THREE.Vector3()
          );
        if (XZ) {
          XZ.x -= this.offsetX;
          XZ.z -= this.offsetZ;
          e.object.position.copy(XZ);
          e.object.position.copy(XZ);
          if (e.object.position.z < 0) {
            e.object.position.z = 0;
          }
        }
      });
    });
  }

  async addObject(obj: AlchemyObject, at: THREE.Vector3) {
    const asset = await obj.getAsset();
    asset.position.copy(at);
    this.add(asset);
    this.objectList.set(asset.id, obj);
    return asset;
    // dragControls = new DragControls(Array.from(this.objectList), )
  }

  dragEnd(target: THREE.Object3D, reset = true) {
    controls.enabled = true;
    const targetObject = this.objectList.get(target.id);

    let closestObject:
      | THREE.Mesh<THREE.BoxGeometry, THREE.MeshLambertMaterial>
      | undefined;
    let bestDistance = Infinity;
    this.objectList.forEach((v) => {
      if (v.asset !== target && v.asset) {
        const dist = v.asset.position.distanceTo(target.position);
        if (dist < bestDistance) {
          bestDistance = dist;
          closestObject = v.asset as THREE.Mesh<
            THREE.BoxGeometry,
            THREE.MeshLambertMaterial
          >;
        }
      }
    });

    if (targetObject && bestDistance < 0.5) {
      const outputType = combine(
        targetObject!.type_id,
        this.objectList.get(closestObject!.id)!.type_id
      );
      if (outputType) {
        const midpoint = target.position
          .clone()
          .add(closestObject!.position!)
          .divideScalar(2);
        const outputObject = instantiate(outputType, midpoint);
        if (outputObject) {
          outputObject.getAsset().then((a) => {
            closestObject?.geometry.dispose();
            if (Array.isArray(closestObject?.material)) {
              closestObject?.material.forEach((material) => {
                if (material.dispose) {
                  material.dispose();
                }
              });
            } else if (
              closestObject?.material &&
              closestObject?.material.dispose
            ) {
              closestObject?.material.dispose();
            }

            if (target instanceof THREE.Mesh) {
              target.geometry.dispose();
              //   target.material.dispose();
              if (Array.isArray(target.material)) {
                target.material.forEach((material: { dispose: () => any }) =>
                  material.dispose()
                );
              } else {
                target.material.dispose();
              }
            }

            this.objectList.delete(closestObject!.removeFromParent().id);
            this.objectList.delete(target.removeFromParent().id);
            this.add(a);
            this.objectList.set(a.id, outputObject);
            dragControls.deactivate();
            dragControls.activate();
            if (reset) {
              this.updateDragControl();
            }
          });

          if (this.unlockObject?.(outputType)) {
            // a new object has been unlocked!
            console.log(`You unlocked ${outputType}!`);
            toast.success(`Unlock ${LIBRARY[outputType].name}!`);

            if (!this.eraOrder) {
              this.eraOrder = 0;
            }
            // Check if there is a next era
            if (
              this.eraOrder !== -1 &&
              this.eraOrder + 1 < Object.values(eraSchemas).length
            ) {
              const nextEra = Object.values(eraSchemas)[this.eraOrder + 1];

              // Check if the outputType is in the requirements of the next era
              if (nextEra.requirements.includes(outputType)) {
                this.setEra?.(this.eraOrder + 1);
                console.log("new era!", this.eraOrder + 1);
                this.eraOrder += 1;
              }
            }
            // Check for scene update
            if (LIBRARY[outputType].scene) {
              // Make sure that we are in right era
              if (this.eraOrder <= LIBRARY[outputType].scene?.eraOrder!) {
                console.log(
                  "era",
                  this.eraOrder,
                  outputType,
                  LIBRARY[outputType].scene?.eraOrder!
                );
                this.sceneNames.forEach((element) => {
                  const scene = this.sceneList.get(element);
                  if (scene) {
                    scene.visible = false;
                  }
                });
                this.sceneList.get(
                  LIBRARY[outputType].scene?.layout!
                )!.visible = true;
                this.setEra?.(LIBRARY[outputType].scene?.eraOrder!);
                this.eraOrder = LIBRARY[outputType].scene?.eraOrder!;
              }
            }
          }
        }
      } else if (reset) {
        target.position.copy(this.dragStartPosition!);
        toast.error(`Sorry, these don't go together!`);
      }
    }
  }

  animate() {
    TWEEN.update();
  }
}

export default GameScene;
